

































































import Vue from 'vue';

// Types and services
import CandidateService, { Candidate, WorkAuthorization } from '~/services/CandidateService';
import IdentityService, { User } from '~/services/IdentityService';
import OrganizationService from '~/services/OrganizationService';
import {
    CandidateProfileScore,
    CandidateProfileScorer,
} from '~/utils/candidate/CandidateProfileScorer';
import { Widget } from '~/services/types/organization';

// Components
import ProfileWrapper from './ProfileWrapper.vue';
import JobPreferences from '~/components/profile/cards/JobPreferences.vue';
import Membership from '~/components/profile/cards/Membership.vue';
import ProfileSkeleton from './ProfileSkeleton.vue';
import ProfileWidgetCard from './components/ProfileWidgetCard.vue';
import WidgetModule from '~/components/smart/WidgetModule.vue';
import EmptyProfileInfo from '~/components/profile/cards/EmptyProfileInfo.vue';
import Languages from '~/components/profile/cards/Languages.vue';
import Links from '~/components/profile/cards/Links.vue';
import Skills from '~/components/profile/cards/Skills.vue';
import StudyPrograms from '~/components/profile/cards/StudyPrograms.vue';
import Summary from '~/components/profile/cards/Summary.vue';
import WorkExperience from '~/components/profile/cards/WorkExperience.vue';

// Constants
const MIDDLE_WIDGET_ID = '9fefd759-b247-4829-9e53-0e93c5a74b54';

export default Vue.extend({
    name: 'Profile',
    components: {
        ProfileWrapper,
        Summary,
        Languages,
        WorkExperience,
        Skills,
        JobPreferences,
        Links,
        StudyPrograms,
        Membership,
        ProfileSkeleton,
        ProfileWidgetCard,
        WidgetModule,
        EmptyProfileInfo,
    },
    data() {
        return {
            identityService: new IdentityService(),
            candidateService: new CandidateService(),
            organizationService: new OrganizationService(),

            user: null as User | null,
            candidate: null as Candidate | null,
            workAuthorizations: null as WorkAuthorization[] | null,

            profileScore: null as CandidateProfileScore | null,
        };
    },
    computed: {
        isLoading(): boolean {
            return !this.user || !this.candidate || !this.workAuthorizations || !this.widgets;
        },
        widgets(): Widget[] {
            return (this.$store.getters['widgetModule/getAllWidgets']() as Widget[] | null) || [];
        },
        bottomWidgets(): Widget[] {
            return this.widgets
                ? this.widgets.filter(widget => widget.id !== MIDDLE_WIDGET_ID)
                : [];
        },
        middleWidget(): Widget | null {
            return this.widgets
                ? this.widgets.find(widget => widget.id === MIDDLE_WIDGET_ID) || null
                : null;
        },
    },
    methods: {
        async loadUser(): Promise<User> {
            this.user = await this.identityService.getMe();
            return this.user;
        },
        async loadCandidate(): Promise<Candidate> {
            this.candidate = await this.candidateService.getMe();
            return this.candidate;
        },
        async loadWorkAuthorizations(): Promise<WorkAuthorization[]> {
            this.workAuthorizations = await this.candidateService.getWorkAuthorizations();
            return this.workAuthorizations;
        },
        refresh(initialLoad = false) {
            if (initialLoad) {
                const user = this.$store.getters['profileModule/getUser']();
                const candidate = this.$store.getters['profileModule/getCandidate']();
                const workAuthorizations = this.$store.getters[
                    'profileModule/getWorkAuthorizations'
                ]();
                if (user && candidate && workAuthorizations) {
                    this.user = user;
                    this.candidate = candidate;
                    this.workAuthorizations = workAuthorizations;
                    this.calculateProfileScore();
                    return;
                }
            }

            Promise.all([
                this.loadUser(),
                this.loadCandidate(),
                this.loadWorkAuthorizations(),
            ]).then(([user, candidate, workAuthorizations]) => {
                this.calculateProfileScore();
                this.$store.commit('profileModule/setUser', user);
                this.$store.commit('profileModule/setCandidate', candidate);
                this.$store.commit('profileModule/setWorkAuthorizations', workAuthorizations);
            });
        },
        refreshProfile() {
            this.refresh(false);
        },
        calculateProfileScore() {
            if (this.candidate && this.user && this.workAuthorizations) {
                this.profileScore = new CandidateProfileScorer(
                    this.candidate,
                    this.user,
                    this.workAuthorizations
                ).getProfileScore();
            }
        },
        openProfileDetailsModal() {
            window.dispatchEvent(new CustomEvent('candidate:profile:details'));
        },
    },
    mounted() {
        this.refresh(true);
        window.addEventListener('candidate:profile:update', this.refreshProfile);
    },
    beforeDestroy() {
        window.removeEventListener('candidate:profile:update', this.refreshProfile);
    },
});
